import { convertLength, FormField, MeasurementInput, Numeric, Select } from "@rothoblaas/design-system";
import { FC, memo, useCallback, useEffect, useMemo } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import FormFieldsContainer from "../../../components/ui/FormFieldsContainer";
import {
  getSingleOptionAutoSelectSignature,
  useSingleOptionAutoSelect,
} from "../../../hooks/useSingleOptionAutoSelect";
import useI18n from "../../../hooks/useTranslations";
import { ConcreteResinFormValues } from "../../../http/concreteResin/types/calculations";
import {
  ChemicalAnchorForSettings,
  ChemicalAnchorMappingForSettings,
} from "../../../http/concreteResin/types/settings";
import { useConcreteResinSettings } from "../../../http/concreteResin/useSettings";
import { keepOnlyForCalculationWhileSelected } from "../../../utils/offeredSettingsItems";
import { inchesToMillimeters, roundMillimeters, toUsDrillBitMillimeters, useIsImperial } from "./resinUnits";

type AutoSelect = ReturnType<typeof useSingleOptionAutoSelect>;

type FieldProps = {
  /** The calculation whose settings (the anchors to choose from) the form shows */
  calculationId?: string;
  /** When true, the field is disabled and nothing is auto-selected */
  readOnly: boolean;
};

// The form always holds mm. Metric shows the hole's lengths in whole mm; imperial in inches, as
// fractions down to the 64th (MeasurementInput), as US drill bits and tapes read them.
const toInches = (millimeters: number | null): number | null =>
  millimeters == null ? null : convertLength(millimeters, "mm", "in");

// A fraction of an inch to the 64th takes 6 decimals (25/64" = 0.390625).
const INCHES_INPUT = { system: "imperial", unit: "in", maxDenominator: 64, decimalScale: 6 } as const;

// Numeric also reports back the value it's given whenever its display changes (a stored value shown
// rounded). Only a different number is a real edit: saving the echo too would overwrite the stored
// mm with its rounded display.
const handleMillimetersChange =
  (millimeters: number | null, onChange: (value: number | null) => void) => (value: number | null) => {
    if (value === (millimeters == null ? null : roundMillimeters(millimeters))) return;
    onChange(value);
  };

const chemicalAnchorLabel = (anchor: ChemicalAnchorForSettings) => anchor.description;

// The thread size (M12), always in mm: imperial converts the hole's lengths only.
const rodDiameterLabel = (mapping: ChemicalAnchorMappingForSettings) => `M${mapping.rodDiameter} mm`;

// One option per rod diameter: the first of its mappings gives its hole diameter.
const getRodDiameterOptions = (
  mappings: readonly ChemicalAnchorMappingForSettings[] | undefined,
): ChemicalAnchorMappingForSettings[] => {
  const uniqueMappings = new Map<number, ChemicalAnchorMappingForSettings>();
  for (const mapping of mappings ?? []) {
    if (!uniqueMappings.has(mapping.rodDiameter)) {
      uniqueMappings.set(mapping.rodDiameter, mapping);
    }
  }
  return Array.from(uniqueMappings.values()).sort((left, right) => left.rodDiameter - right.rodDiameter);
};

// The hole the rod is drilled in: its mapping's metric hole, or in imperial the US drill bit for it
// (exact mm: the calculation stays in mm), as no US bit drills the metric hole.
const getHoleDiameter = (mapping: ChemicalAnchorMappingForSettings, isImperial: boolean): number =>
  isImperial ? toUsDrillBitMillimeters(mapping.holeDiameter) : mapping.holeDiameter;

// The selected anchor's rod diameters, from the settings shared with the rest of the page.
const useRodDiameterOptions = (calculationId: string | undefined) => {
  const { data: settings, isLoading } = useConcreteResinSettings(calculationId);
  const chemicalAnchorId = useWatch<ConcreteResinFormValues, "chemicalAnchorId">({ name: "chemicalAnchorId" });
  const mappings = settings?.chemicalAnchors.find((anchor) => anchor.id === chemicalAnchorId)?.mappings;
  const options = useMemo(() => getRodDiameterOptions(mappings), [mappings]);

  return { chemicalAnchorId, options, isLoading, isLoaded: !!settings };
};

const ChemicalAnchorField: FC<FieldProps & { autoSelect: AutoSelect }> = ({ calculationId, readOnly, autoSelect }) => {
  const i18n = useI18n();
  const { control, setValue } = useFormContext<ConcreteResinFormValues>();
  const { shouldAutoSelectSingleOption, rememberSingleOptionChange } = autoSelect;
  const { data: settings, isLoading } = useConcreteResinSettings(calculationId);
  const chemicalAnchorId = useWatch({ control, name: "chemicalAnchorId" });

  // A hidden anchor the calculation uses is offered only while still selected.
  const options = useMemo(
    () =>
      keepOnlyForCalculationWhileSelected(settings?.chemicalAnchors ?? [], (anchor) => anchor.id === chemicalAnchorId),
    [chemicalAnchorId, settings],
  );
  const signature = useMemo(() => getSingleOptionAutoSelectSignature(options, (anchor) => anchor.id), [options]);
  const singleOption = options.length === 1 ? options[0] : undefined;
  const isAutoSelected = !!singleOption && chemicalAnchorId === singleOption.id;

  // Another anchor has other rods: the rod and its hole are picked again.
  const selectAnchor = useCallback(
    (anchorId: string | null) => {
      setValue("chemicalAnchorId", anchorId, { shouldDirty: true, shouldValidate: true });
      setValue("rodDiameter", null, { shouldDirty: true });
      setValue("holeDiameter", null, { shouldDirty: true });
    },
    [setValue],
  );

  useEffect(() => {
    if (readOnly || !singleOption || isAutoSelected || !shouldAutoSelectSingleOption("chemicalAnchorId", signature)) {
      return;
    }
    selectAnchor(singleOption.id);
  }, [isAutoSelected, readOnly, selectAnchor, shouldAutoSelectSingleOption, signature, singleOption]);

  return (
    <Controller
      name="chemicalAnchorId"
      control={control}
      rules={{ required: true }}
      render={({ field, fieldState }) => (
        <FormField
          label={i18n.translation.concreteResinModule.chemicalAnchor}
          error={fieldState.error}
          info={isAutoSelected ? i18n.translation.common.singleOptionAvailable : undefined}
          locale={i18n.translation.languageCode}
        >
          {({ labelId, isInvalid }) => (
            <Select<ChemicalAnchorForSettings, false>
              {...field}
              inputId={labelId}
              value={options.find((anchor) => anchor.id === field.value) ?? null}
              options={options}
              getOptionValue={(option) => option.id}
              getOptionLabel={chemicalAnchorLabel}
              placeholder={`${i18n.translation.common.select}...`}
              isLoading={isLoading}
              isDisabled={isLoading || readOnly || isAutoSelected}
              isInvalid={isInvalid}
              onChange={(option) => {
                rememberSingleOptionChange("chemicalAnchorId", signature, option);
                selectAnchor(option?.id ?? null);
              }}
            />
          )}
        </FormField>
      )}
    />
  );
};

const RodDiameterField: FC<FieldProps & { autoSelect: AutoSelect }> = ({ calculationId, readOnly, autoSelect }) => {
  const i18n = useI18n();
  const { control, setValue } = useFormContext<ConcreteResinFormValues>();
  const { shouldAutoSelectSingleOption, rememberSingleOptionChange } = autoSelect;
  const { chemicalAnchorId, options, isLoading, isLoaded } = useRodDiameterOptions(calculationId);
  const [rodDiameter, holeDiameter] = useWatch({ control, name: ["rodDiameter", "holeDiameter"] });
  const isImperial = useIsImperial();

  const signature = useMemo(
    () => getSingleOptionAutoSelectSignature(options, (option) => `${option.rodDiameter}`, chemicalAnchorId ?? ""),
    [chemicalAnchorId, options],
  );
  const singleOption = options.length === 1 ? options[0] : undefined;
  const isAutoSelected = !!singleOption && rodDiameter === singleOption.rodDiameter;

  // The rod and the hole it's drilled in.
  const selectRod = useCallback(
    (mapping: ChemicalAnchorMappingForSettings | null) => {
      setValue("rodDiameter", mapping?.rodDiameter ?? null, { shouldDirty: true, shouldValidate: true });
      setValue("holeDiameter", mapping ? getHoleDiameter(mapping, isImperial) : null, { shouldDirty: true });
    },
    [isImperial, setValue],
  );

  // A loaded calculation's rod without an anchor, or one its anchor no longer has (once the settings
  // tell), is cleared with its hole, as is a hole left without its rod: the server takes no hole
  // without its rod. Not validated: no error before the user touches them.
  useEffect(() => {
    if (readOnly) return;
    if (rodDiameter == null) {
      if (holeDiameter != null) setValue("holeDiameter", null, { shouldDirty: true });
      return;
    }
    if (!chemicalAnchorId || (isLoaded && !options.some((option) => option.rodDiameter === rodDiameter))) {
      setValue("rodDiameter", null, { shouldDirty: true });
      setValue("holeDiameter", null, { shouldDirty: true });
    }
  }, [chemicalAnchorId, holeDiameter, isLoaded, options, readOnly, rodDiameter, setValue]);

  // After the clearing above, which would otherwise undo it in the same commit; again on a rod change,
  // as when that clearing comes from a later commit.
  useEffect(() => {
    if (readOnly || !singleOption || isAutoSelected || !shouldAutoSelectSingleOption("rodDiameter", signature)) {
      return;
    }
    selectRod(singleOption);
  }, [isAutoSelected, readOnly, rodDiameter, selectRod, shouldAutoSelectSingleOption, signature, singleOption]);

  return (
    <Controller
      name="rodDiameter"
      control={control}
      rules={{ required: true }}
      render={({ field, fieldState }) => (
        <FormField
          label={i18n.translation.concreteResinModule.rodDiameter}
          error={fieldState.error}
          info={isAutoSelected ? i18n.translation.common.singleOptionAvailable : undefined}
          locale={i18n.translation.languageCode}
        >
          {({ labelId, isInvalid }) => (
            <Select<ChemicalAnchorMappingForSettings, false>
              {...field}
              inputId={labelId}
              value={options.find((option) => option.rodDiameter === field.value) ?? null}
              options={options}
              getOptionValue={(option) => `${option.rodDiameter}`}
              getOptionLabel={rodDiameterLabel}
              placeholder={`${i18n.translation.common.select}...`}
              isLoading={isLoading}
              isDisabled={!chemicalAnchorId || isLoading || options.length === 0 || readOnly || isAutoSelected}
              isInvalid={isInvalid}
              onChange={(option) => {
                rememberSingleOptionChange("rodDiameter", signature, option);
                selectRod(option);
              }}
            />
          )}
        </FormField>
      )}
    />
  );
};

// Disabled: the hole the selected rod is drilled in.
const HoleDiameterField: FC<FieldProps> = ({ calculationId, readOnly }) => {
  const i18n = useI18n();
  const { control, setValue } = useFormContext<ConcreteResinFormValues>();
  const { options } = useRodDiameterOptions(calculationId);
  const [rodDiameter, holeDiameter] = useWatch({ control, name: ["rodDiameter", "holeDiameter"] });
  const isImperial = useIsImperial();

  // A unit system switch changes the hole the rod is drilled in (see getHoleDiameter): follow it,
  // and on a calculation whose hole doesn't match its rod's yet. Not on a read-only one, which can't
  // save it. Compared within a tolerance: the stored mm round-trip through the database. Not marked
  // dirty: no measurement was typed, so a loaded saved entry stays highlighted.
  const selectedMapping = options.find((option) => option.rodDiameter === rodDiameter);
  const expectedHoleDiameter = selectedMapping ? getHoleDiameter(selectedMapping, isImperial) : null;
  useEffect(() => {
    if (readOnly || expectedHoleDiameter == null || holeDiameter == null) return;
    if (Math.abs(holeDiameter - expectedHoleDiameter) > 1e-6) {
      setValue("holeDiameter", expectedHoleDiameter);
    }
  }, [expectedHoleDiameter, holeDiameter, readOnly, setValue]);

  return (
    <Controller
      name="holeDiameter"
      control={control}
      rules={{ min: 0.001 }}
      render={({ field, fieldState }) => (
        <FormField
          label={i18n.translation.concreteResinModule.holeDiameter}
          error={fieldState.error}
          locale={i18n.translation.languageCode}
        >
          {/* in imperial the US drill bit the hole is drilled with, as its fraction (13/32) */}
          {({ labelId, isInvalid }) =>
            isImperial ? (
              <MeasurementInput
                {...INCHES_INPUT}
                id={labelId}
                value={toInches(field.value)}
                isInvalid={isInvalid}
                disabled
              />
            ) : (
              <Numeric
                {...field}
                value={field.value == null ? null : roundMillimeters(field.value)}
                decimalScale={0}
                suffix={i18n.translation.common.unitMm}
                id={labelId}
                isInvalid={isInvalid}
                allowNegative={false}
                disabled
              />
            )
          }
        </FormField>
      )}
    />
  );
};

const HoleDepthField: FC<Pick<FieldProps, "readOnly">> = ({ readOnly }) => {
  const i18n = useI18n();
  const { control } = useFormContext<ConcreteResinFormValues>();
  const isImperial = useIsImperial();

  return (
    <Controller
      name="holeDepth"
      control={control}
      rules={{ required: true, min: 0.001 }}
      render={({ field, fieldState }) => (
        <FormField
          label={i18n.translation.concreteResinModule.holeDepth}
          error={fieldState.error}
          locale={i18n.translation.languageCode}
        >
          {/* imperial takes fractions (4 21/64), saved as mm. Only a typed value changes it: on blur
              MeasurementInput reports its rounded display, which would rewrite the stored mm. */}
          {({ labelId, isInvalid }) =>
            isImperial ? (
              <MeasurementInput
                {...INCHES_INPUT}
                id={labelId}
                value={toInches(field.value)}
                isInvalid={isInvalid}
                disabled={readOnly}
                onValueChange={(inches) => field.onChange(inches == null ? null : inchesToMillimeters(inches))}
                onBlur={() => field.onBlur()}
              />
            ) : (
              <Numeric
                {...field}
                value={field.value == null ? null : roundMillimeters(field.value)}
                decimalScale={0}
                suffix={i18n.translation.common.unitMm}
                id={labelId}
                isInvalid={isInvalid}
                allowNegative={false}
                disabled={readOnly}
                onValueChange={handleMillimetersChange(field.value, field.onChange)}
              />
            )
          }
        </FormField>
      )}
    />
  );
};

// The hole geometry: each field watches only what it shows, so typing in one re-renders it alone.
const ConcreteResinForm: FC<FieldProps> = ({ calculationId, readOnly }) => {
  const i18n = useI18n();
  const resinTranslation = i18n.translation.concreteResinModule;
  const { control } = useFormContext<ConcreteResinFormValues>();
  const autoSelect = useSingleOptionAutoSelect();

  return (
    <FormFieldsContainer fullWidth>
      <ChemicalAnchorField calculationId={calculationId} readOnly={readOnly} autoSelect={autoSelect} />
      <RodDiameterField calculationId={calculationId} readOnly={readOnly} autoSelect={autoSelect} />
      <HoleDiameterField calculationId={calculationId} readOnly={readOnly} />
      <HoleDepthField readOnly={readOnly} />

      {/* No. OF HOLES */}
      <Controller
        name="holeNumbers"
        control={control}
        rules={{ required: true, min: 0.001 }}
        render={({ field, fieldState }) => (
          <FormField
            label={resinTranslation.holeNumbers}
            error={fieldState.error}
            locale={i18n.translation.languageCode}
          >
            {({ labelId, isInvalid }) => (
              <Numeric
                {...field}
                value={field.value}
                decimalScale={0}
                suffix={i18n.translation.common.unitPcs}
                id={labelId}
                isInvalid={isInvalid}
                allowNegative={false}
                disabled={readOnly}
                onValueChange={(value) => field.onChange(value != null ? Math.trunc(value) : null)}
              />
            )}
          </FormField>
        )}
      />

      {/* OVERAGE FACTOR */}
      <Controller
        name="overageFactor"
        control={control}
        rules={{ required: false, min: 0.0 }}
        render={({ field, fieldState }) => (
          <FormField
            label={resinTranslation.overageFactor}
            error={fieldState.error}
            locale={i18n.translation.languageCode}
            isOptional
          >
            {({ labelId, isInvalid }) => (
              <Numeric
                {...field}
                value={field.value}
                decimalScale={3}
                suffix={resinTranslation.unitOverageFactor}
                id={labelId}
                isInvalid={isInvalid}
                allowNegative={false}
                disabled={readOnly}
                onValueChange={(value) => field.onChange(value)}
              />
            )}
          </FormField>
        )}
      />
    </FormFieldsContainer>
  );
};

export default memo(ConcreteResinForm);
